import type { CollectionsListQueryParams } from '@/lib/api';
import { urlContentExistence } from '@/lib/content-existence-filter';
import { TABLE_PAGING_URL } from '@/lib/table-paging';
import { urlColsMask, urlList, urlParam, urlSort, urlText } from '@/lib/url-state/codecs';
import type { UrlValues } from '@/lib/url-state/url-schema';

export const COLLECTION_TYPES = ['smart', 'manual'] as const;

/** Columns the list sorts by (their DataTable ids, which the API takes as `sortBy`). */
export const COLLECTIONS_BULK_EDIT_SORT_COLUMNS = [
  'fullname',
  'title',
  'products_count',
  'updated_at',
] as const;

type SortColumn = (typeof COLLECTIONS_BULK_EDIT_SORT_COLUMNS)[number];

const SORT = urlSort({
  columns: COLLECTIONS_BULK_EDIT_SORT_COLUMNS,
  default: { id: 'fullname', dir: 'asc' },
  legacy: { field: 'sortBy', direction: 'sortDir' },
});

/**
 * `sort=<col>.<dir>`. Before T-196 the page wrote `sortBy` and `sortDir`, and read `sortField` and
 * `sortDirection` too; all four are still read.
 */
const urlCollectionsSort: typeof SORT = {
  ...SORT,
  keys: [...SORT.keys, 'sortField', 'sortDirection'],
  read(params) {
    if (params.has('sort') || params.has('sortBy')) return SORT.read(params);
    if (!params.has('sortField') && !params.has('sortDirection')) return undefined;
    const legacy = new URLSearchParams();
    legacy.set('sortBy', params.get('sortField') ?? 'fullname');
    legacy.set('sortDir', params.get('sortDirection') ?? 'asc');
    return SORT.read(legacy);
  },
};

/** Published to the Online Store: true, false, or null for no filter. */
const urlPublished = urlParam<boolean | null>({
  key: 'published',
  default: null,
  parse: (raw) => (raw === 'true' ? true : raw === 'false' ? false : undefined),
  format: (value) => String(value),
});

/**
 * /collections-bulk-edit in the URL. Until T-196 the keys were `search` (or `query`), `type`,
 * `pageSize` and `sortBy`/`sortDir`; they're still read, so bookmarks and old links open the same
 * list (decision 30 c). `cols` comes only from a link or a saved view.
 */
export const COLLECTIONS_BULK_EDIT_URL = {
  q: urlText('q', { aliases: ['search', 'query'] }),
  types: urlList('types', { aliases: ['type'], values: COLLECTION_TYPES }),
  published: urlPublished,
  seoTitle: urlContentExistence('seoTitle'),
  seoDescription: urlContentExistence('seoDescription'),
  description: urlContentExistence('description'),
  sort: urlCollectionsSort,
  cols: urlColsMask(),
  ...TABLE_PAGING_URL,
};

export type CollectionsBulkEditView = UrlValues<typeof COLLECTIONS_BULK_EDIT_URL>;

/** Remembered per team: the sidebar brings back the last filters and sort. */
export const COLLECTIONS_BULK_EDIT_VIEW_ID = 'collections.bulkEdit';

/** The list request for a view (the caller adds `page` and `pageSize`). */
export function collectionsBulkEditListQuery(
  view: CollectionsBulkEditView
): CollectionsListQueryParams {
  return {
    search: view.q || undefined,
    type: view.types.length > 0 ? view.types : undefined,
    published: view.published ?? undefined,
    seoTitle: view.seoTitle ?? undefined,
    seoDescription: view.seoDescription ?? undefined,
    description: view.description ?? undefined,
    sortBy: view.sort.id as SortColumn,
    sortDir: view.sort.dir,
  };
}
